import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import { Icons } from '@ohif/ui-next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faListCheck,
  faClipboardList,
  faCircleExclamation,
  faUsers,
  faClock,
  faBolt,
  faFlag,
  faChartLine,
} from '@fortawesome/free-solid-svg-icons';
import AppHeader from '../../components/AppHeader/AppHeader';
import AppTooltip from '../../components/AppTooltip/AppTooltip';
import InvalidDicomFilesModal from '../../components/InvalidDicomFilesModal';
import './Qc.css';
import ApiService from '../../services/ApiService';
import { formatNameByCountry } from '../../utils/nameFormatter';
import loadingGif from '../../../assets/loading.gif';

function polarToCartesian(cx: number, cy: number, radius: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(rad),
    y: cy + radius * Math.sin(rad),
  };
}

function describeDonutSegment(
  cx: number,
  cy: number,
  innerRadius: number,
  outerRadius: number,
  startAngle: number,
  endAngle: number
): string {
  const sweep = Math.min(Math.max(endAngle - startAngle, 0.01), 359.99);
  const end = startAngle + sweep;
  const outerStart = polarToCartesian(cx, cy, outerRadius, startAngle);
  const outerEnd = polarToCartesian(cx, cy, outerRadius, end);
  const innerEnd = polarToCartesian(cx, cy, innerRadius, end);
  const innerStart = polarToCartesian(cx, cy, innerRadius, startAngle);
  const largeArc = sweep > 180 ? 1 : 0;

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

function formatMissingFieldParamLabel(paramKey: string): string {
  return paramKey
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

function ClockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

type InterGradingAgreementChartItem = {
  graderId: string;
  name: string;
  agreementPerc: number;
};

type StatisticalAgreementTooltipProps = {
  active?: boolean;
  payload?: Array<{ value?: number }>;
  label?: string;
};

function StatisticalAgreementTooltip({
  active,
  payload,
  label,
}: StatisticalAgreementTooltipProps) {
  if (!active || !payload?.length) {
    return null;
  }

  const value = Number(payload[0]?.value) || 0;

  return (
    <div className="qc-agreement-tooltip">
      <div className="qc-agreement-tooltip-title">{label}</div>
      <div className="qc-agreement-tooltip-row">
        <span className="qc-agreement-tooltip-dot" aria-hidden />
        <span>{value}%</span>
      </div>
    </div>
  );
}

type ProductivityMetricItem = {
  graderId: string;
  name: string;
  averageTimeSec: number;
  status: string;
};

type SubmissionOption = {
  id: string;
  label: string;
};

type RadarGraderSeries = {
  key: string;
  name: string;
  color: string;
  graderId: string;
};

type QcViewStudiesIconButtonProps = {
  icon: IconDefinition;
  onClick: () => void;
  ariaLabel: string;
  className?: string;
  tooltip?: string;
};

const QcViewStudiesIconButton = ({
  icon,
  onClick,
  ariaLabel,
  className = 'qc-metric-view-btn',
  tooltip = 'View studies',
}: QcViewStudiesIconButtonProps) => (
  <AppTooltip content={tooltip} side="top" sideOffset={6}>
    <button type="button" className={className} onClick={onClick} aria-label={ariaLabel}>
      <FontAwesomeIcon icon={icon} className="qc-view-icon" />
    </button>
  </AppTooltip>
);

type QcTimeMetricPileProps = {
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
  icon?: IconDefinition;
  onClick?: () => void;
  ariaLabel?: string;
  className?: string;
};

const QcTimeMetricPile = ({
  label,
  value,
  valueClassName = '',
  icon,
  onClick,
  ariaLabel,
  className = '',
}: QcTimeMetricPileProps) => {
  const pileClassName = ['qc-time-pile', className].filter(Boolean).join(' ');

  const content = (
    <>
      <div className="qc-time-pile-header">
        <span className="qc-time-pile-label">{label}</span>
        {icon ? <FontAwesomeIcon icon={icon} className="qc-metric-card-view-icon" aria-hidden /> : null}
      </div>
      <div className={`qc-time-pile-value ${valueClassName}`.trim()}>{value}</div>
    </>
  );

  if (!onClick) {
    return <div className={pileClassName}>{content}</div>;
  }

  return (
    <AppTooltip content="View studies" side="top" sideOffset={6}>
      <div
        className={`${pileClassName} qc-time-pile-navigable`.trim()}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick();
          }
        }}
        aria-label={ariaLabel}
      >
        {content}
      </div>
    </AppTooltip>
  );
};

type QcTimeBarRowProps = {
  label: string;
  valueSec: number;
  fillClassName: string;
  maxSec: number;
};

const QcTimeBarRow = ({ label, valueSec, fillClassName, maxSec }: QcTimeBarRowProps) => {
  const widthPct = `${Math.min((valueSec / maxSec) * 100, 100)}%`;
  const displaySec = Math.round(valueSec);

  return (
    <div className="qc-time-row">
      <span className="qc-time-label">{label}</span>
      <AppTooltip content={`${label}: ${displaySec} sec`} side="top" sideOffset={6}>
        <div
          className="qc-time-bar-track"
          role="img"
          aria-label={`${label}: ${displaySec} seconds`}
        >
          <div className={`qc-time-bar-fill ${fillClassName}`} style={{ width: widthPct }} />
        </div>
      </AppTooltip>
    </div>
  );
};

type DeviationFlagChartTickProps = {
  x?: number;
  y?: number;
  payload?: { value?: string };
  onNavigate: (label: string) => void;
};

const DeviationFlagChartTick = ({ x = 0, y = 0, payload, onNavigate }: DeviationFlagChartTickProps) => {
  const value = payload?.value ?? '';
  const normalized = String(value).trim().toLowerCase();
  const isClickable = normalized === 'time' || normalized === 'annotation';
  const icon = normalized === 'time' ? faClock : normalized === 'annotation' ? faFlag : null;

  if (!isClickable || !icon) {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          fill="#e5e7eb"
          fontSize={10}
          transform="rotate(-20)"
        >
          {value}
        </text>
      </g>
    );
  }

  return (
    <g transform={`translate(${x},${y})`}>
      <foreignObject x={-52} y={4} width={104} height={32} style={{ overflow: 'visible' }}>
        <div
          xmlns="http://www.w3.org/1999/xhtml"
          className="qc-dev-tick"
          role="button"
          tabIndex={0}
          onClick={() => onNavigate(String(value))}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onNavigate(String(value));
            }
          }}
          aria-label={`View ${value} studies`}
        >
          <FontAwesomeIcon icon={icon} className="qc-dev-tick-icon" />
          <span className="qc-dev-tick-label">{value}</span>
        </div>
      </foreignObject>
    </g>
  );
};

const ProductivityMetrics: React.FC<{
  data: ProductivityMetricItem[];
}> = ({ data }) => {
  const values = data.map(item => item.averageTimeSec).filter(v => v > 0);
  const minValue = values.length ? Math.min(...values) : 0;
  const maxValue = values.length ? Math.max(...values) : 30;

  // Build domain only from average_time_sec values.
  const spread = Math.max(maxValue - minValue, 30);
  const paddedMin = Math.max(0, minValue - spread * 0.15);
  const paddedMax = maxValue + spread * 0.15;
  const xAxisMin = Math.floor(paddedMin / 30) * 30;
  const xAxisMax = Math.ceil(paddedMax / 30) * 30;

  return (
    <section className="qc-productivity-card qc-lower-card">
      <h4 className="qc-subsection-title">Productivity Metrics</h4>
      <div className="qc-productivity-inner">
        <div className="qc-productivity-chart">
          <ResponsiveContainer width="100%" height={150}>
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 10, right: 16, left: 0, bottom: 30 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                horizontal={false}
                stroke="#1f2937"
              />
              <XAxis
                type="number"
                domain={[xAxisMin, xAxisMax]}
                tick={{ fill: '#94a3b8', fontSize: 11 }}
                axisLine={false}
                tickFormatter={value => `${value}`}
                label={{
                  value: 'Avg time (sec)',
                  position: 'insideBottomRight',
                  offset: -4,
                  fill: '#94a3b8',
                  fontSize: 10,
                }}
              />
              <YAxis
                type="category"
                dataKey="name"
                interval={0}
                tick={{ fill: '#e5e7eb', fontSize: 13 }}
                width={65}
                axisLine={false}
              />
              <Tooltip
                cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }}
                formatter={(value: number) => [`${Number(value).toFixed(2)} sec`, 'Avg Time/Study']}
                contentStyle={{
                  backgroundColor: '#020617',
                  border: '1px solid #1f2937',
                  borderRadius: 6,
                  fontSize: 11,
                  color: '#e5e7eb',
                }}
              />
              <Bar dataKey="averageTimeSec" fill="#60a5fa" barSize={16} radius={[8, 8, 8, 8]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="qc-status-chips">
          {data.map(item => {
            const normalizedStatus = (item.status || '').toLowerCase();
            const statusClass = normalizedStatus.includes('delay')
              ? 'qc-orange'
              : normalizedStatus.includes('on-time') || normalizedStatus.includes('on time')
                ? 'qc-green'
                : 'qc-lightblue';

            return (
              <div key={item.graderId} className="qc-status-chip">
                <span className="qc-status-chip-title">{item.name}</span>
                <span className={`qc-status-chip-value ${statusClass}`}>
                  {item.status || 'N/A'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Qc: React.FC = () => {
  const navigate = useNavigate();
  const apiService = ApiService.getInstance();
  const { submission_id } = useParams<{ submission_id: string }>();
  const [submissionOptions, setSubmissionOptions] = useState<SubmissionOption[]>([]);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string>('');
  const [submissionBasedQc, setSubmissionBasedQc] = useState<any | null>(null);
  const [graderBasedQc, setGraderBasedQc] = useState<any | null>(null);
  const [graderFullNamesById, setGraderFullNamesById] = useState<Record<string, string>>({});
  const [graderFirstNamesById, setGraderFirstNamesById] = useState<Record<string, string>>({});
  const [qcLoading, setQcLoading] = useState<boolean>(Boolean(submission_id));
  const [radarVisibleGraderIds, setRadarVisibleGraderIds] = useState<Record<string, boolean>>({});
  const [showInvalidFilesModal, setShowInvalidFilesModal] = useState(false);

  const effectiveSubmissionId = (submission_id ?? selectedSubmissionId ?? '').toString().trim();

  const invalidFilesSubmissionTitle = useMemo(() => {
    if (!effectiveSubmissionId) {
      return '';
    }
    const match = submissionOptions.find(option => option.id === effectiveSubmissionId);
    return match?.label || effectiveSubmissionId;
  }, [effectiveSubmissionId, submissionOptions]);

  const handleNavigateToPendingStudies = () => {
    if (!effectiveSubmissionId) {
      return;
    }

    const query = new URLSearchParams();
    query.set('submission_id', effectiveSubmissionId);
    query.set('status', 'PENDING');

    navigate(`/studiesList?${query.toString()}`, { state: { from: 'qc' } });
  };

  const handleNavigateToMissingFieldsStudies = () => {
    const effectiveSubmissionId = (submission_id ?? selectedSubmissionId ?? '').toString().trim();
    if (!effectiveSubmissionId) {
      return;
    }

    const query = new URLSearchParams();
    query.set('submission_id', effectiveSubmissionId);
    query.set('has_missing_fields', 'true');

    navigate(`/studiesList?${query.toString()}`, { state: { from: 'qc' } });
  };

  const handleNavigateToSubmissionStudies = () => {
    const effectiveSubmissionId = (submission_id ?? selectedSubmissionId ?? '').toString().trim();
    if (!effectiveSubmissionId) {
      return;
    }

    const query = new URLSearchParams();
    query.set('submission_id', effectiveSubmissionId);

    navigate(`/studiesList?${query.toString()}`, { state: { from: 'qc' } });
  };

  const handleNavigateToExcessiveTimeStudies = () => {
    const effectiveSubmissionId = (submission_id ?? selectedSubmissionId ?? '').toString().trim();
    if (!effectiveSubmissionId) {
      return;
    }

    const query = new URLSearchParams();
    query.set('submission_id', effectiveSubmissionId);
    query.set('is_excessive', 'true');

    navigate(`/studiesList?${query.toString()}`, { state: { from: 'qc' } });
  };

  const handleNavigateToTooFastStudies = () => {
    const effectiveSubmissionId = (submission_id ?? selectedSubmissionId ?? '').toString().trim();
    if (!effectiveSubmissionId) {
      return;
    }

    const query = new URLSearchParams();
    query.set('submission_id', effectiveSubmissionId);
    query.set('is_too_fast', 'true');

    navigate(`/studiesList?${query.toString()}`, { state: { from: 'qc' } });
  };

  const handleNavigateToEscalatedStudies = () => {
    const effectiveSubmissionId = (submission_id ?? selectedSubmissionId ?? '').toString().trim();
    if (!effectiveSubmissionId) {
      return;
    }

    const query = new URLSearchParams();
    query.set('submission_id', effectiveSubmissionId);
    query.set('is_escalated', 'true');

    navigate(`/studiesList?${query.toString()}`, { state: { from: 'qc' } });
  };

  const handleNavigateToGraderOutliersStudies = ({
    graderId,
    outlierFilter,
  }: {
    graderId: string;
    outlierFilter: 'has_outliers' | 'is_excessive' | 'is_too_fast';
  }) => {
    const effectiveSubmissionId = (submission_id ?? selectedSubmissionId ?? '').toString().trim();
    const effectiveGraderId = String(graderId ?? '').trim();
    if (!effectiveSubmissionId || !effectiveGraderId) {
      return;
    }

    const query = new URLSearchParams();
    query.set('submission_id', effectiveSubmissionId);
    query.set('grader_id', effectiveGraderId);
    query.set(outlierFilter, 'true');

    navigate(`/studiesList?${query.toString()}`, { state: { from: 'qc' } });
  };

  const handleNavigateFromDeviationFlags = (label: string) => {
    const effectiveSubmissionId = (submission_id ?? selectedSubmissionId ?? '').toString().trim();
    if (!effectiveSubmissionId) {
      return;
    }

    const normalized = (label || '').toString().trim().toLowerCase();
    const query = new URLSearchParams();
    query.set('submission_id', effectiveSubmissionId);

    if (normalized === 'time') {
      query.set('has_outliers', 'true');
    } else if (normalized === 'annotation') {
      query.set('is_flagged', 'true');
    } else {
      return;
    }

    navigate(`/studiesList?${query.toString()}`, { state: { from: 'qc' } });
  };

  const normalizeSubmissionOption = (item: any): SubmissionOption | null => {
    if (item === null || item === undefined) {
      return null;
    }

    if (typeof item === 'string' || typeof item === 'number') {
      const id = String(item).trim();
      if (!id) {
        return null;
      }
      return { id, label: id };
    }

    if (typeof item === 'object') {
      const idCandidate =
        item.submission_id ??
        item.id ??
        item.submissionId ??
        item.submissionID;
      const id = String(idCandidate ?? '').trim();
      if (!id) {
        return null;
      }
      const label =
        String(
          item.job_title ??
            item.title ??
            item.name ??
            item.submission_name ??
            id
        ).trim() || id;
      return { id, label };
    }

    return null;
  };

  const extractSubmissionOptions = (rawData: any): SubmissionOption[] => {
    let parsed = rawData;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        parsed = [parsed];
      }
    }

    const sourceArray = Array.isArray(parsed)
      ? parsed
      : Array.isArray(parsed?.submissions)
        ? parsed.submissions
        : [];

    const normalized = sourceArray
      .map(normalizeSubmissionOption)
      .filter(Boolean) as SubmissionOption[];

    const dedupedById = new Map<string, SubmissionOption>();
    normalized.forEach(option => {
      if (!dedupedById.has(option.id)) {
        dedupedById.set(option.id, option);
      }
    });

    return Array.from(dedupedById.values());
  };

  useEffect(() => {
    setSelectedSubmissionId(submission_id ?? '');
  }, [submission_id]);

  useEffect(() => {
    let isMounted = true;

    const fetchSubmissionList = async () => {
      try {
        const baseUrl = apiService.getGradingServiceUrl();
        const response = await apiService.get<any>('/submissions/list', undefined, baseUrl);
        if (!isMounted || !response?.success) {
          return;
        }

        const responseData = response.data as any;
        const options = extractSubmissionOptions(responseData?.data);
        setSubmissionOptions(options);
      } catch (error) {
        console.warn('Failed to load submission list for QC switcher:', error);
      }
    };

    fetchSubmissionList();

    return () => {
      isMounted = false;
    };
  }, [apiService]);

  useEffect(() => {
    const fetchQcInit = async () => {
      try {
        setQcLoading(true);
        const baseUrl = apiService.getGradingServiceUrl();
        const endpoint = `submissions/initiate-qc?submission_id=${encodeURIComponent(
          submission_id ?? ''
        )}`;

        // submission_id is passed as query param; body kept empty for now
        const response = await apiService.get<any>(endpoint, undefined, baseUrl);

        // For now, just log the raw API response so we can inspect it
        console.log('QC initiate API response', response);

        const responseBody = response?.data as any;
        const qcResults = responseBody?.data?.[0]?.qc_results ?? {};
        const nextSubmissionBasedQc = qcResults?.submission_based_qc ?? null;
        const nextGraderBasedQc = qcResults?.grader_based_qc ?? null;

        setSubmissionBasedQc(nextSubmissionBasedQc);
        setGraderBasedQc(nextGraderBasedQc);
      } catch (error) {
        console.error('Error calling QC initiate API', error);
      } finally {
        setQcLoading(false);
      }
    };

    if (submission_id) {
      fetchQcInit();
    } else {
      console.warn('QC route loaded without submission_id param');
      setQcLoading(false);
    }
  }, [apiService, submission_id]);

  useEffect(() => {
    let isMounted = true;

    const fetchGraders = async () => {
      try {
        const baseUrl = apiService.getAuthServiceUrl();
        const response = await apiService.get<any>('/grader/get-users', undefined, baseUrl);
        if (!isMounted || !response?.success) {
          return;
        }

        const responseData = response.data as any;
        const rawData = Array.isArray(responseData?.data)
          ? responseData.data
          : Array.isArray(responseData)
            ? responseData
            : [];

        let usersList: any[] = [];
        rawData.forEach(item => {
          if (Array.isArray(item?.users)) {
            usersList = usersList.concat(item.users);
          } else if (item) {
            usersList.push(item);
          }
        });
        if (Array.isArray(responseData?.users)) {
          usersList = usersList.concat(responseData.users);
        }

        const { fullMap, firstMap } = usersList.reduce(
          (acc, userEntry) => {
            const firstName = (userEntry?.first_name ?? userEntry?.firstName ?? '').toString().trim();
            const lastName = (
              userEntry?.last_name ??
              userEntry?.lastName ??
              userEntry?.surname ??
              userEntry?.family_name ??
              ''
            )
              .toString()
              .trim();
            const middleName = (userEntry?.middle_name ?? userEntry?.middleName ?? '').toString().trim();
            const explicitFull = (
              userEntry?.full_name ??
              userEntry?.fullName ??
              userEntry?.display_name ??
              userEntry?.displayName ??
              ''
            )
              .toString()
              .trim();
            const apiName = (userEntry?.name ?? '').toString().trim();
            const partJoin = [firstName, middleName, lastName].filter(Boolean).join(' ').trim();
            const formattedName = formatNameByCountry(firstName, lastName, middleName, 'IND');
            const fromFormatter = formattedName && formattedName !== '-' ? formattedName : '';

            const fromParts =
              explicitFull || partJoin || fromFormatter || '';
            const usernameOrEmail = (userEntry?.username || userEntry?.email || '').toString().trim();
            const apiLooksFuller =
              Boolean(apiName) &&
              /\s/.test(apiName) &&
              (!fromParts || !/\s/.test(fromParts));

            const displayName =
              (apiLooksFuller ? apiName : fromParts) ||
              (apiLooksFuller ? '' : apiName) ||
              usernameOrEmail;

            const idCandidates = [
              userEntry?.id,
              userEntry?.user_id,
              userEntry?.ID,
              userEntry?.grader_id,
              userEntry?.graderId,
            ].filter(v => v !== null && v !== undefined && String(v).trim() !== '');

            if (idCandidates.length === 0) {
              return acc;
            }

            const primaryId = String(idCandidates[0]).trim();
            const graderFallback = `Grader ${primaryId}`;
            const fullLabel = displayName || graderFallback;
            const isGenericGraderLabel = fullLabel === graderFallback || /^Grader\s+/i.test(fullLabel);
            const firstOnly = firstName
              ? firstName
              : !fullLabel || isGenericGraderLabel
                ? graderFallback
                : fullLabel.trim().split(/\s+/).filter(Boolean)[0] || graderFallback;

            idCandidates.forEach(raw => {
              const k = String(raw).trim();
              if (k) {
                acc.fullMap[k] = fullLabel;
                acc.firstMap[k] = firstOnly;
              }
            });

            return acc;
          },
          { fullMap: {} as Record<string, string>, firstMap: {} as Record<string, string> }
        );

        setGraderFullNamesById(fullMap);
        setGraderFirstNamesById(firstMap);
      } catch (error) {
        console.warn('Failed to load grader list for QC:', error);
      }
    };

    fetchGraders();

    return () => {
      isMounted = false;
    };
  }, [apiService]);

  const studiesReceived = useMemo(() => {
    const expectedStudyCount =
      Number(submissionBasedQc?.expected_study_count) || 0;
    const totalStudyCount =
      Number(submissionBasedQc?.total_study_count) || 0;
    const missingStudyCount = Math.max(expectedStudyCount - totalStudyCount, 0);

    return {
      expectedStudyCount,
      totalStudyCount,
      missingStudyCount,
    };
  }, [submissionBasedQc]);

  const workloadLabelFromId = (graderId: string, nameMap: Record<string, string>) => {
    const key = String(graderId).trim();
    if (nameMap[key]) {
      return nameMap[key];
    }
    const n = Number(key);
    if (!Number.isNaN(n) && nameMap[String(n)] !== undefined) {
      return nameMap[String(n)];
    }
    return `Grader ${graderId}`;
  };

  const dicomValidity = useMemo(() => {
    const validCount = Number(submissionBasedQc?.dicom_valid_count) || 0;
    const invalidCount = Number(submissionBasedQc?.dicom_invalid_count) || 0;
    const total = validCount + invalidCount;
    const validPercent = total > 0 ? (validCount / total) * 100 : 0;

    return {
      validCount,
      invalidCount,
      total,
      validPercent,
    };
  }, [submissionBasedQc]);

  const handleOpenInvalidFilesModal = () => {
    if (!effectiveSubmissionId) {
      return;
    }
    setShowInvalidFilesModal(true);
  };

  const deviationFlagsChart = useMemo(() => {
    const timeFlagged = Number(submissionBasedQc?.deviation_flags?.time_flagged) || 0;
    const annotationFlagged =
      Number(submissionBasedQc?.deviation_flags?.annotation_flagged) || 0;
    const totalFlagged = Number(submissionBasedQc?.deviation_flags?.total_flagged) || 0;
    const maxVal = Math.max(timeFlagged, annotationFlagged, totalFlagged, 0);
    const headroom = maxVal > 0 ? Math.max(1, Math.ceil(maxVal * 0.2)) : 1;
    const yMax = maxVal + headroom;

    return {
      data: [
        { name: 'Time', value: timeFlagged },
        { name: 'Annotation', value: annotationFlagged },
        { name: 'Total Flagged', value: totalFlagged },
      ],
      yMax,
    };
  }, [submissionBasedQc]);

  const annotationCompleteness = useMemo(() => {
    const ac = submissionBasedQc?.annotations_completeness ?? {};
    const completedPerGrader =
      (ac?.completed_study_per_grader &&
        typeof ac.completed_study_per_grader === 'object')
        ? (ac.completed_study_per_grader as Record<string, any>)
        : {};

    const pendingStudyCount = Number(ac?.pending_study_count) || 0;
    const missingFieldsCount =
      Number(ac?.missing_fields?.count ?? ac?.missing_fields_count) || 0;
    const missingFieldsStudyCount =
      Number(ac?.missing_fields?.missing_fields_study_count) || 0;
    const missingFieldParams = (() => {
      const params = ac?.missing_fields?.params;
      if (!params || typeof params !== 'object') {
        return [] as Array<{ key: string; label: string; count: number }>;
      }

      return Object.entries(params as Record<string, unknown>)
        .map(([key, value]) => ({
          key,
          label: formatMissingFieldParamLabel(key),
          count: Number(value) || 0,
        }))
        .filter(item => item.count > 0);
    })();
    const completedStudyCount = Number(ac?.completed_study_count) || 0;

    const passesInitiated =
      Number((submissionBasedQc as any)?.total_pass_initiated) ||
      Number(submissionBasedQc?.passes_initiated) ||
      0;

    const totalStudies = Number(submissionBasedQc?.total_study_count) || 0;

    return {
      completedPerGrader,
      pendingStudyCount,
      missingFieldsCount,
      missingFieldsStudyCount,
      missingFieldParams,
      completedStudyCount,
      passesInitiated,
      totalStudies,
    };
  }, [submissionBasedQc]);

  const qcTimeThresholds = useMemo(() => {
    const performanceThresholds = graderBasedQc?.performance_thresholds;
    const fastSec = Number(performanceThresholds?.threshold_fast_sec);
    const delayedSec = Number(performanceThresholds?.threshold_delayed_sec);

    const secondsToMinutes = (seconds: number) => {
      if (!Number.isFinite(seconds) || seconds <= 0) {
        return 0;
      }
      return Math.round((seconds / 60) * 10) / 10;
    };

    const submissionSource = submissionBasedQc ?? {};
    const legacyFastMin =
      Number(submissionSource.fast_threshold) ||
      Number(submissionSource.time_based_qc?.fast_threshold) ||
      0;
    const legacySlowMin =
      Number(submissionSource.slow_threshold) ||
      Number(submissionSource.time_based_qc?.slow_threshold) ||
      0;

    const fastFromApi = secondsToMinutes(fastSec);
    const slowFromApi = secondsToMinutes(delayedSec);

    return {
      fast: fastFromApi > 0 ? fastFromApi : legacyFastMin,
      slow: slowFromApi > 0 ? slowFromApi : legacySlowMin,
    };
  }, [graderBasedQc, submissionBasedQc]);

  const productivityMetricsData = useMemo(() => {
    const source = graderBasedQc?.productivity_metrics;
    if (!source || typeof source !== 'object') {
      return [];
    }

    return Object.entries(source as Record<string, any>).map(([graderId, metric]) => ({
      graderId,
      name: graderFirstNamesById[graderId] || `Grader ${graderId}`,
      averageTimeSec: Number(metric?.average_time_sec) || 0,
      status: String(metric?.status || ''),
    }));
  }, [graderBasedQc, graderFirstNamesById]);

  const timeBasedPerformanceData = useMemo(() => {
    const source = graderBasedQc?.time_based_performance;
    if (!source || typeof source !== 'object') {
      return [];
    }

    return Object.entries(source as Record<string, any>).map(([graderId, perf]) => ({
      graderId,
      name: graderFirstNamesById[graderId] || `Grader ${graderId}`,
      avgTimeMin: Number(perf?.avg_time_min) || 0,
      outliers: Number(perf?.outliers) || 0,
      excessive: Number(perf?.excessive_gt_expected_plus_90s) || 0,
      tooFast: Number(perf?.too_fast_lt_10s) || 0,
    }));
  }, [graderBasedQc, graderFirstNamesById]);

  const annotationQualityMetricsData = useMemo(() => {
    const source = graderBasedQc?.annotation_quality_metrics;
    if (!source || typeof source !== 'object') {
      return [];
    }

    return Object.entries(source as Record<string, any>).map(([graderId, metric]) => ({
      graderId,
      name: graderFirstNamesById[graderId] || `Grader ${graderId}`,
      escalationCount: Number(metric?.escalation_count) || 0,
      reversalCount: Number(metric?.reversal_count) || 0,
      completenessPerc: Number(metric?.completeness_perc) || 0,
      outliersCount:
        Number(metric?.flagged_count ?? metric?.annotation_outlier_studies) || 0,
    }));
  }, [graderBasedQc, graderFirstNamesById]);

  const interGradingAgreement = useMemo(() => {
    const source = graderBasedQc?.statistical_agreement?.inter_grading_agreement;
    if (!source || typeof source !== 'object') {
      return null;
    }

    const enabled = Boolean(source.enabled);
    const chartData: InterGradingAgreementChartItem[] = enabled
      ? Object.entries((source.per_grader || {}) as Record<string, any>).map(
          ([graderId, metric]) => ({
            graderId,
            name: graderFirstNamesById[graderId] || `Grader ${graderId}`,
            agreementPerc: Number(metric?.agreement_perc) || 0,
          })
        )
      : [];

    return {
      enabled,
      reason: String(source.reason || ''),
      overallAgreementPerc:
        source.overall_agreement_perc == null
          ? null
          : Number(source.overall_agreement_perc),
      chartData,
    };
  }, [graderBasedQc, graderFirstNamesById]);

  const escalationReviewRadar = useMemo(() => {
    const source = graderBasedQc?.review;
    if (!source || typeof source !== 'object') {
      return {
        data: [],
        graders: [] as RadarGraderSeries[],
      };
    }

    const palette = ['#60a5fa', '#22c55e', '#f97316', '#a855f7', '#ef4444'];
    const selectedGraders = Object.entries(source as Record<string, any>).slice(0, 5);

    const graders: RadarGraderSeries[] = selectedGraders.map(([graderId], idx) => ({
      key: `g${idx + 1}`,
      name: graderFirstNamesById[graderId] || `Grader ${graderId}`,
      color: palette[idx % palette.length],
      graderId,
    }));

    const metricKeys = [
      { subject: 'Consistency', apiKey: 'consistency_perc' },
      { subject: 'Time Stability', apiKey: 'time_stability_perc' },
      { subject: 'Completeness', apiKey: 'completeness_perc' }, // replaced Protocol with Completeness
      { subject: 'Productivity', apiKey: 'productivity_perc' },
      { subject: 'Escalation', apiKey: 'escalation_perc' },
    ];

    const data = metricKeys.map(metric => {
      const row: Record<string, any> = { subject: metric.subject };
      graders.forEach(grader => {
        const graderReview = (source as Record<string, any>)[grader.graderId] ?? {};
        row[grader.key] = Number(graderReview?.[metric.apiKey]) || 0;
      });
      return row;
    });

    return { data, graders };
  }, [graderBasedQc, graderFirstNamesById]);

  const radarGraderIdsKey = useMemo(
    () => escalationReviewRadar.graders.map(g => g.graderId).join(','),
    [escalationReviewRadar.graders]
  );

  useEffect(() => {
    const graderIds = radarGraderIdsKey ? radarGraderIdsKey.split(',') : [];
    const next: Record<string, boolean> = {};
    graderIds.forEach(id => {
      next[id] = true;
    });
    setRadarVisibleGraderIds(next);
  }, [submission_id, radarGraderIdsKey]);

  const visibleRadarGraders = useMemo(
    () =>
      escalationReviewRadar.graders.filter(
        grader => radarVisibleGraderIds[grader.graderId] !== false
      ),
    [escalationReviewRadar.graders, radarVisibleGraderIds]
  );

  const radarChartData = useMemo(
    () =>
      escalationReviewRadar.data.map(row => ({
        ...row,
        __outline__: 100,
      })),
    [escalationReviewRadar.data]
  );

  const showRadarOutlineOnly =
    visibleRadarGraders.length === 0 && escalationReviewRadar.graders.length > 0;

  const behaviouralQcData = useMemo(() => {
    const source = graderBasedQc?.behavioural_qc;
    if (!source || typeof source !== 'object') {
      return [];
    }

    return Object.entries(source as Record<string, any>).map(([graderId, item]) => ({
      graderId,
      name: graderFirstNamesById[graderId] || `Grader ${graderId}`,
      loginStability: String(item?.login_stability || ''),
      sessionAdherence: String(item?.session_adherence || ''),
      stabilityScore: Number(item?.stability_score) || 0,
    }));
  }, [graderBasedQc, graderFirstNamesById]);

  const graderQcScoreData = useMemo(() => {
    const qcResults = graderBasedQc?.qc_results;
    const gradingConfig = graderBasedQc?.grading_config;

    if (!qcResults || typeof qcResults !== 'object' || !Array.isArray(gradingConfig)) {
      return { graders: [] as Array<any>, buckets: [] as Array<any> };
    }

    const buckets = (gradingConfig as Array<any>).map(cfg => ({
      id: String(cfg?.id ?? ''),
      min: Number(cfg?.min_score) || 0,
      max: Number(cfg?.max_score) || 0,
      color: String(cfg?.color || '#4b5563'),
      description: String(cfg?.description || ''),
    }));

    const graders = Object.entries(qcResults as Record<string, any>).map(([graderId, result]) => {
      const score = Number((result as any)?.score) || 0;
      const matchedBucket =
        buckets.find(b => score >= b.min && score <= b.max) ??
        null;

      return {
        graderId,
        name: graderFirstNamesById[graderId] || `Grader ${graderId}`,
        score,
        bucketId: matchedBucket?.id ?? null,
        color: matchedBucket?.color ?? '#4b5563',
      };
    });

    return { graders, buckets };
  }, [graderBasedQc, graderFirstNamesById]);

  const workloadDistribution = useMemo(() => {
    const src = graderBasedQc?.workload_distribution_perc;
    const productivityMetrics = graderBasedQc?.productivity_metrics;
    if (!src || typeof src !== 'object') {
      return { segments: [] as any[], total: 0 };
    }

    const palette = [
      { color: '#60a5fa', className: 'qc-blue' },
      { color: '#22c55e', className: 'qc-green' },
      { color: '#f97316', className: 'qc-orange' },
      { color: '#a855f7', className: 'qc-magenta' },
      { color: '#ef4444', className: 'qc-red' },
    ];

    let idx = 0;
    const segments = Object.entries(src as Record<string, any>).map(([graderId, value]) => {
      const paletteItem = palette[idx % palette.length];
      const metric =
        productivityMetrics && typeof productivityMetrics === 'object'
          ? (productivityMetrics as Record<string, any>)[graderId]
          : null;
      idx += 1;
      const percent = Number(value) || 0;
      return {
        id: graderId,
        label: workloadLabelFromId(graderId, graderFirstNamesById),
        value: percent,
        assignedStudyCount: Number(metric?.assigned_study_count) || 0,
        submittedStudyCount: Number(metric?.submitted_study_count) || 0,
        color: paletteItem.color,
        colorClass: paletteItem.className,
      };
    });

    const total = segments.reduce((sum, s) => sum + s.value, 0);
    return { segments, total };
  }, [graderBasedQc, graderFirstNamesById]);

  return (
    <div className="qc-container">
      <AppHeader />
      <div className="qc-content">
        {qcLoading ? (
          <div className="loading-container">
            <img src={loadingGif} alt="Loading..." className="loading-gif" />
          </div>
        ) : (
          <>
            <div className="qc-topbar">
              <button type="button" className="qc-back-button" onClick={() => navigate('/submissions')}>
                <Icons.ChevronLeft />
                <span>Back to Submissions</span>
              </button>
              <div className="qc-submission-switcher">
                <label htmlFor="qc-submission-select" className="qc-submission-switcher-label">
                  Submission
                </label>
                <select
                  id="qc-submission-select"
                  className="qc-submission-select"
                  value={selectedSubmissionId}
                  onChange={event => {
                    const nextSubmissionId = event.target.value;
                    setSelectedSubmissionId(nextSubmissionId);
                    if (nextSubmissionId && nextSubmissionId !== submission_id) {
                      navigate(`/${encodeURIComponent(nextSubmissionId)}/qc`);
                    }
                  }}
                >
                  {!selectedSubmissionId && (
                    <option value="" disabled>
                      Select submission
                    </option>
                  )}
                  {submissionOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="qc-top-sections">
              <div className="qc-sections">
          <div className="qc-left-column">
            <section className="qc-section-card">
            <h3 className="qc-section-title">Submission Based QC</h3>
            <p className="qc-section-desc">Quality control metrics for the submitted batch</p>

            <div className="qc-kpi-grid qc-kpi-grid-summary">
              <div className="qc-kpi-card">
                <div className="qc-kpi-head">
                  <span className="qc-kpi-label">Avg Time/Study</span>
                  <span className="qc-kpi-icon" aria-hidden>
                    <ClockIcon />
                  </span>
                </div>
                <div className="qc-kpi-value">
                  <span className="qc-kpi-value-primary qc-lightblue">
                    {(Number(submissionBasedQc?.avg_time_sec) || 0).toFixed(0)}
                  </span>
                  <span className="qc-kpi-value-secondary qc-lightblue"> sec</span>
                </div>
                <div className="qc-kpi-foot">
                  Expected:{' '}
                  <span className="qc-magenta">
                    {(Number(submissionBasedQc?.expected_time_sec) || 0).toFixed(0)} sec
                  </span>
                </div>
              </div>

              <div className="qc-kpi-card">
                <div className="qc-kpi-head">
                  <span className="qc-kpi-label">Outliers Flagged</span>
                  <span className="qc-kpi-icon" aria-hidden>
                    <Icons.FlagPennant />
                  </span>
                </div>
                <div className="qc-kpi-value">
                  {(() => {
                    const flagged = Number(submissionBasedQc?.flagged_study_count) || 0;
                    const total = Number(submissionBasedQc?.total_study_count) || 0;
                    const perc = total > 0 ? (flagged / total) * 100 : 0;
                    return (
                      <span className="qc-kpi-value-primary qc-red">{perc.toFixed(1)}</span>
                    );
                  })()}
                  <span className="qc-kpi-value-secondary qc-red">%</span>
                </div>
                <div className="qc-kpi-foot">
                  <span className="qc-red">{Number(submissionBasedQc?.flagged_study_count) || 0}/</span>
                  <span className="qc-lightblue">{Number(submissionBasedQc?.total_study_count) || 0} </span>studies
                </div>
              </div>
            </div>

            <div className="qc-submission-grid">
              <div className="qc-submission-column">
                <h4 className="qc-subsection-title">Submission Completeness</h4>

                <div className="qc-subgrid qc-subgrid-top">
                  <div className="qc-metric-card">
                    <div className="qc-metric-title">Studies</div>
                    <div className="qc-metric-line">
                      <span className="qc-lightblue">
                        {studiesReceived.expectedStudyCount || 0}
                      </span>
                      <span className="qc-foot-grey"> Expected</span>
                    </div>
                    <div className="qc-metric-line">
                      <span className="qc-green">
                        {studiesReceived.totalStudyCount || 0}
                      </span>
                      <span className="qc-foot-grey"> Received</span>
                    </div>
                    <div className="qc-metric-line">
                      <span className="qc-red">
                        {studiesReceived.missingStudyCount}
                      </span>
                      <span className="qc-foot-grey"> Missing</span>
                    </div>
                  </div>

                  <div className="qc-metric-card">
                    <div className="qc-metric-title">DICOM Validity</div>
                    <div className="qc-metric-line qc-foot-grey">
                      <span className="qc-green">{dicomValidity.validPercent.toFixed(1)}</span>
                      <span className="qc-foot-grey">% valid</span>
                    </div>
                    <div className="qc-metric-line qc-foot-grey">
                      <span className="qc-green">{dicomValidity.validCount} </span>Valid
                    </div>
                    <div className="qc-metric-line qc-metric-line-with-action qc-foot-grey">
                      <button
                        type="button"
                        className="qc-metric-link"
                        onClick={handleOpenInvalidFilesModal}
                        aria-label="View invalid DICOM files"
                      >
                        <span className="qc-red">{dicomValidity.invalidCount}</span>
                      </button>
                      <span> Invalid</span>
                      <QcViewStudiesIconButton
                        icon={faListCheck}
                        onClick={handleOpenInvalidFilesModal}
                        ariaLabel="View invalid DICOM files"
                        tooltip="View invalid files"
                      />
                    </div>
                  </div>
                </div>

                <div className="qc-subgrid qc-subgrid-top">
                  <div className="qc-metric-card">
                    <div className="qc-metric-title">Modalities</div>
                    {(() => {
                      const modalityObj = submissionBasedQc?.modality;
                      const entries = modalityObj && typeof modalityObj === 'object'
                        ? Object.entries(modalityObj as Record<string, any>)
                        : [];

                      if (entries.length === 0) {
                        return (
                          <div className="qc-metric-line qc-foot-grey">
                            No modalities
                          </div>
                        );
                      }

                      return entries.map(([key, value]) => (
                        <div key={key} className="qc-metric-line">
                          <span className="qc-lightblue">{key}</span>
                          <span className="qc-foot-grey"> : </span>
                          <span className="qc-green">{Number(value) || 0}</span>
                        </div>
                      ));
                    })()}
                  </div>

                  <div className="qc-metric-card">
                    <div className="qc-metric-title">No of duplicates detected</div>
                    <div className="qc-metric-line qc-foot-grey">
                      <span className="qc-red">{Number(submissionBasedQc?.duplicate_dicom_count) || 0} </span>
                      duplicates detected
                    </div>
                  </div>
                </div>
              </div>

              <div className="qc-submission-column">
                <h4 className="qc-subsection-title">Annotation Completeness</h4>

                <div className="qc-subgrid qc-subgrid-top">
                  <div className="qc-metric-card qc-metric-card-wide">
                    <div className="qc-metric-title">Completed work</div>
                    {(() => {
                      const entries = Object.entries(annotationCompleteness.completedPerGrader);
                      if (entries.length === 0) {
                        return (
                          <div className="qc-metric-line qc-foot-grey">
                            No completed-work data
                          </div>
                        );
                      }

                      return (
                        <>
                          <div className="qc-completed-work-grid">
                            {entries.map(([graderId, perc]) => (
                              <div key={graderId} className="qc-metric-line">
                                <span className="qc-lightblue">{Number(perc) || 0}%</span>
                                <span className="qc-foot-grey">
                                  {' '}
                                  {workloadLabelFromId(String(graderId), graderFullNamesById)}
                                </span>
                              </div>
                            ))}
                          </div>
                          <div className="qc-metric-line qc-metric-line-with-action">
                            <button
                              type="button"
                              className="qc-metric-link"
                              onClick={handleNavigateToPendingStudies}
                              aria-label="View pending studies"
                            >
                              <span className="qc-red">{annotationCompleteness.pendingStudyCount}</span>
                            </button>
                            <span className="qc-foot-grey"> Pending</span>
                            <QcViewStudiesIconButton
                              icon={faListCheck}
                              onClick={handleNavigateToPendingStudies}
                              ariaLabel="View pending studies"
                            />
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  <AppTooltip content="View studies" side="top" sideOffset={6}>
                    <div
                      className="qc-metric-card qc-metric-card-navigable"
                      role="button"
                      tabIndex={0}
                      onClick={handleNavigateToMissingFieldsStudies}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleNavigateToMissingFieldsStudies();
                        }
                      }}
                      aria-label="View studies with missing fields"
                    >
                      <div className="qc-metric-card-header">
                        <div className="qc-metric-title">Missing fields</div>
                        <FontAwesomeIcon
                          icon={faClipboardList}
                          className="qc-metric-card-view-icon"
                          aria-hidden
                        />
                      </div>
                      <div className="qc-metric-line qc-foot-grey">
                        <span className="qc-red">{annotationCompleteness.missingFieldsCount}</span> Fields
                        {annotationCompleteness.missingFieldsStudyCount > 0 ? (
                          <>
                            {' '}
                            in{' '}
                            <span className="qc-red">
                              {annotationCompleteness.missingFieldsStudyCount}
                            </span>{' '}
                            {annotationCompleteness.missingFieldsStudyCount === 1
                              ? 'study'
                              : 'studies'}
                          </>
                        ) : null}
                      </div>
                      {annotationCompleteness.missingFieldParams.length === 0 ? (
                        <div className="qc-metric-line qc-foot-grey">No parameter breakdown</div>
                      ) : (
                        annotationCompleteness.missingFieldParams.map(param => (
                          <div key={param.key} className="qc-metric-line">
                            <span>{param.label}</span>
                            <span className="qc-foot-grey">: </span>
                            <span className="qc-red">{param.count}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </AppTooltip>

                  <div className="qc-metric-card">
                    <div className="qc-metric-title">Pass</div>
                    {(() => {
                      const total = annotationCompleteness.totalStudies;
                      const passCount = Math.max(annotationCompleteness.passesInitiated, 0);
                      if (!passCount) {
                        return (
                          <div className="qc-metric-line qc-foot-grey">
                            No pass data
                          </div>
                        );
                      }

                      return Array.from({ length: passCount }).map((_, idx) => (
                        <div key={idx} className="qc-metric-line">
                          <span className="qc-green">{total}</span>/
                          <span className="qc-lightblue">{total}</span>
                          <span className="qc-foot-grey"> Pass {idx + 1}</span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                <div className="qc-subgrid qc-subgrid-bottom">
                  {/* Arbitration completed card commented out for now (removed from DOM to avoid blank space) */}
                </div>
              </div>
            </div>

            <div className="qc-lower-sections">
                <section className="qc-lower-card qc-lower-card-span-full">
                  <h3 className="qc-section-title">Time-Based QC</h3>

                  <div className="qc-time-bars">
                    {(() => {
                      const expectedSec = Number(submissionBasedQc?.expected_time_sec) || 0;
                      const observedSec = Number(submissionBasedQc?.avg_time_sec) || 0;
                      const rawMax = Math.max(expectedSec, observedSec, 1);

                      // "Nice" max so axis ticks are clean (multiples of 30s)
                      const niceMax = Math.max(30, Math.ceil(rawMax / 30) * 30);
                      const ticks = [0, niceMax * 0.25, niceMax * 0.5, niceMax * 0.75, niceMax];

                      return (
                        <>
                    <QcTimeBarRow
                      label="Expected"
                      valueSec={expectedSec}
                      fillClassName="qc-time-bar-expected"
                      maxSec={niceMax}
                    />
                    <QcTimeBarRow
                      label="Observed"
                      valueSec={observedSec}
                      fillClassName="qc-time-bar-observed"
                      maxSec={niceMax}
                    />
                    <div className="qc-time-axis">
                      {ticks.map(t => (
                        <span key={t}>{`${Math.round(t)}s`}</span>
                      ))}
                    </div>
                        </>
                      );
                    })()}
                  </div>

                  <div className="qc-time-pile-row">
                    <QcTimeMetricPile
                      label={`Excessive time (>${qcTimeThresholds.slow} min)`}
                      value={Number(submissionBasedQc?.time_based_qc?.excessive_time_count) || 0}
                      valueClassName="qc-orange"
                      icon={faClock}
                      onClick={handleNavigateToExcessiveTimeStudies}
                      ariaLabel="View excessive time studies"
                    />
                    <QcTimeMetricPile
                      label={`Too fast (<${qcTimeThresholds.fast} min)`}
                      value={Number(submissionBasedQc?.time_based_qc?.too_fast_count) || 0}
                      valueClassName="qc-orange"
                      icon={faBolt}
                      onClick={handleNavigateToTooFastStudies}
                      ariaLabel="View too fast studies"
                    />
                    <QcTimeMetricPile
                      label="Time distribution stability"
                      value={`${(
                        Number(
                          submissionBasedQc?.time_based_qc?.time_distribution_stability ??
                            submissionBasedQc?.time_based_qc?.time_distribution_stability_sec
                        ) || 0
                      ).toFixed(2)}%`}
                      valueClassName="qc-lightblue"
                      icon={faChartLine}
                    />
                  </div>
                </section>

                <section className="qc-lower-card qc-lower-card-span-full">
                  <h3 className="qc-section-title">Deviation Flags</h3>
                  <p className="qc-section-desc">Counts of flagged instances (not percentage)</p>

                  <div className="qc-dev-chart">
                    <ResponsiveContainer width="100%" height={140}>
                      <BarChart
                        data={deviationFlagsChart.data}
                        margin={{ top: 10, right: 12, left: 0, bottom: 20 }}
                      >
                        <CartesianGrid stroke="#1f2937" vertical={false} />
                        <XAxis
                          dataKey="name"
                          tick={props => (
                            <DeviationFlagChartTick
                              {...props}
                              onNavigate={handleNavigateFromDeviationFlags}
                            />
                          )}
                          axisLine={{ stroke: '#374151' }}
                          tickLine={{ stroke: '#374151' }}
                          interval={0}
                          height={52}
                          tickMargin={6}
                        />
                        <YAxis
                          type="number"
                          domain={[0, deviationFlagsChart.yMax]}
                          allowDecimals={false}
                          tick={{ fill: '#94a3b8', fontSize: 10 }}
                          axisLine={{ stroke: '#374151' }}
                          tickLine={{ stroke: '#374151' }}
                        />
                        <Tooltip
                          cursor={{ fill: 'rgba(148,163,184,0.12)' }}
                          formatter={(value: number) => [`${value}`, 'Count']}
                          contentStyle={{
                            backgroundColor: '#020617',
                            border: '1px solid #1f2937',
                            borderRadius: 6,
                            fontSize: 11,
                            color: '#e5e7eb',
                          }}
                        />
                        <Bar
                          dataKey="value"
                          fill="#f97316"
                          radius={[4, 4, 0, 0]}
                          barSize={22}
                          label={{ position: 'top', fill: '#e5e7eb', fontSize: 10 }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="qc-dev-text">
                    <div className="qc-metric-line qc-foot-grey">Frequency of anomalies</div>
                    <div className="qc-metric-line">
                      {(() => {
                        const flagged = Number(submissionBasedQc?.flagged_study_count) || 0;
                        const total = Number(submissionBasedQc?.total_study_count) || 0;
                        const perc = total > 0 ? (flagged / total) * 100 : 0;
                        return (
                          <>
                            <span className="qc-red">
                              {flagged}/{total} studies
                            </span>
                            <span className="qc-foot-grey"> = </span>
                            <span className="qc-red">{perc.toFixed(1)}%</span>
                            <span className="qc-foot-grey"> flagged</span>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </section>

                <section className="qc-lower-card qc-lower-card-span-full">
                  <div className="qc-section-title-row">
                    <h3 className="qc-section-title">Escalation Review</h3>
                    <QcViewStudiesIconButton
                      icon={faCircleExclamation}
                      onClick={handleNavigateToEscalatedStudies}
                      ariaLabel="View escalated studies"
                      className="qc-section-view-btn"
                    />
                  </div>

                  {(() => {
                    const escalation = submissionBasedQc?.review?.escalation;
                    const labels: string[] = Array.isArray(escalation?.labels) ? escalation.labels : [];
                    const values: number[] = Array.isArray(escalation?.data) ? escalation.data : [];
                    const total = Number(escalation?.total) || 0;

                    const colorByLabel: Record<string, { color: string; className: string }> = {
                      CLINICAL: { color: '#60a5fa', className: 'qc-blue' },
                      TECHNICAL: { color: '#22c55e', className: 'qc-green' },
                      OTHERS: { color: '#f97316', className: 'qc-orange' },
                    };

                    const segments = labels
                      .map((label, idx) => ({
                        label,
                        value: Number(values[idx]) || 0,
                        ...(colorByLabel[String(label).toUpperCase()] ?? { color: '#1f2937', className: 'qc-foot-grey' }),
                      }))
                      .filter(s => s.value > 0);

                    let currentAngle = 0;
                    const gradientParts = (segments.length ? segments : [{ label: 'NONE', value: 1, color: '#1f2937', className: 'qc-foot-grey' }]).map(
                      seg => {
                        const denom = total > 0 ? total : 1;
                        const angle = (seg.value / denom) * 360;
                        const start = currentAngle;
                        const end = start + angle;
                        currentAngle = end;
                        return `${seg.color} ${start}deg ${end}deg`;
                      }
                    );

                    return (
                      <div className="qc-escalation-grid qc-escalation-grid-single">
                        <div className="qc-escalation-column">
                          <div className="qc-escalation-heading">By Category</div>
                          <div
                            className="qc-donut"
                            style={{ background: `conic-gradient(${gradientParts.join(', ')})` }}
                          >
                            <div className="qc-donut-inner">
                              <span className="qc-donut-count">{total}</span>
                              <span className="qc-donut-label">Total</span>
                            </div>
                          </div>

                          {segments.length === 0 ? (
                            <div className="qc-escalation-legend qc-foot-grey">No escalations</div>
                          ) : (
                            segments.map(seg => (
                              <div key={seg.label} className="qc-escalation-legend">
                                <span className={seg.className}>{seg.value}</span>
                                <span className="qc-foot-grey"> {String(seg.label).toLowerCase()}</span>
                              </div>
                            ))
                          )}
                        </div>

                        {/* Arbitration Result hidden for now */}
                      </div>
                    );
                  })()}
                </section>
              </div>
            </section>
        </div>

        <section className="qc-section-card qc-section-card-right">
          <div className="qc-section-title-row">
            <h3 className="qc-section-title">Grader-Based QC</h3>
            <QcViewStudiesIconButton
              icon={faUsers}
              onClick={handleNavigateToSubmissionStudies}
              ariaLabel="View submission studies"
              className="qc-section-view-btn"
            />
          </div>
          <p className="qc-section-desc">Individual grader performance and quality metrics</p>

          {/* top summary kpis */}
          <div className="qc-kpi-grid qc-kpi-grid-right">
            <div className="qc-kpi-card">
              <div className="qc-kpi-head">
                <span className="qc-kpi-label">Total Studies Graded</span>
                <span className="qc-kpi-icon" aria-hidden>
                  <Icons.Clipboard />
                </span>
              </div>
              <div className="qc-kpi-value">
                  <span className="qc-kpi-value-primary qc-green">
                    {Number(graderBasedQc?.total_study_graded) || 0}
                  </span>
              </div>
              <div className="qc-kpi-foot qc-foot-grey">
                Across{' '}
                <span className="qc-red">
                  {Number(graderBasedQc?.grader_count) || 0}{' '}
                </span>
                graders
              </div>
            </div>

            <div className="qc-kpi-card">
              <div className="qc-kpi-head">
                <span className="qc-kpi-label">Top Performer</span>
                <span className="qc-kpi-icon" aria-hidden>
                  <Icons.Clipboard />
                </span>
              </div>
              <div className="qc-kpi-value">
                <span className="qc-kpi-value-primary qc-lightblue">
                  {graderBasedQc?.top_performer
                    ? workloadLabelFromId(String(graderBasedQc.top_performer.grader_id), graderFirstNamesById)
                    : '—'}
                </span>
              </div>
              <div className="qc-kpi-foot">
                <span className="qc-green">
                  {graderBasedQc?.top_performer
                    ? `${Number(
                        graderBasedQc.top_performer.score ??
                          graderBasedQc.top_performer.score_perc ??
                          0
                      ).toFixed(0)}%`
                    : '—'}
                </span>{' '}
                QC Score
              </div>
            </div>

            {/* Avg Inter-Grader Term card removed */}

            <div className="qc-kpi-card">
              <div className="qc-kpi-head">
                <span className="qc-kpi-label">Total Escalations</span>
                <span className="qc-kpi-icon" aria-hidden>
                  <Icons.Clipboard />
                </span>
              </div>
              <div className="qc-kpi-value">
                <span className="qc-kpi-value-primary qc-red">
                  {Number(graderBasedQc?.escalations?.total_count) || 0}
                </span>
              </div>
              <div className="qc-kpi-foot">
                {(() => {
                  const escalationsByGrader = graderBasedQc?.escalations?.grader_count ?? {};
                  const entries = Object.entries(escalationsByGrader as Record<string, any>);
                  if (entries.length === 0) {
                    return <span className="qc-foot-grey">No grader escalations</span>;
                  }

                  return entries.map(([graderId, count], idx) => (
                    <span key={graderId}>
                      {idx > 0 ? ' ' : ''}
                      <span className="qc-red">{Number(count) || 0} </span>
                      <span className="qc-foot-grey">
                        {graderFirstNamesById[String(graderId)] || `Grader ${graderId}`}
                      </span>
                    </span>
                  ));
                })()}
              </div>
            </div>
          </div>

          {/* second tier charts and tables */}
          <div className="qc-right-blocks qc-right-blocks-three">
            <section className="qc-workload-card qc-lower-card">
              <h4 className="qc-subsection-title">Workload Distribution</h4>

              <div className="qc-workload-content">
                {(() => {
                const total = workloadDistribution.total || 0;
                const rawSegments = workloadDistribution.segments;
                const hasData = total > 0 && rawSegments.length > 0;

                const segments = hasData
                  ? rawSegments.map(seg => ({
                      ...seg,
                      percent: total > 0 ? Math.round((seg.value / total) * 100) : 0,
                    }))
                  : [];

                let currentAngle = 0;
                const segmentsWithAngles =
                  hasData
                    ? segments.map(segment => {
                        const start = currentAngle;
                        const angle = (segment.value / total) * 360;
                        const end = start + angle;
                        currentAngle = end;
                        const centerDeg = start + angle / 2;
                        const centerRad = (centerDeg * Math.PI) / 180;
                        return {
                          ...segment,
                          startAngle: start,
                          endAngle: end,
                          gradientPart: `${segment.color} ${start}deg ${end}deg`,
                          centerRad,
                        };
                      })
                    : [];
                const donutCx = 55;
                const donutCy = 55;
                const donutOuterR = 55;
                const donutInnerR = 32;

                const gradientParts = hasData
                  ? segmentsWithAngles.map(s => s.gradientPart)
                  : ['#1f2937 0deg 360deg'];
                const donutRadiusPercent = 50;
                const labelRadiusRatio = 1.2;
                const labelOffsetPercent = donutRadiusPercent * labelRadiusRatio;

                const labelPosition = (centerRad: number) => ({
                  left: `${50 + labelOffsetPercent * Math.sin(centerRad)}%`,
                  top: `${50 - labelOffsetPercent * Math.cos(centerRad)}%`,
                });

                return (
                  <>
                    <div className="qc-workload-donut">
                      <div
                        className="qc-donut qc-donut-workload"
                        style={{ background: `conic-gradient(${gradientParts.join(', ')})` }}
                      >
                        <div className="qc-donut-inner" />
                      </div>

                      {hasData && (
                        <svg
                          className="qc-workload-donut-overlay"
                          viewBox="0 0 110 110"
                          aria-hidden
                        >
                          {segmentsWithAngles.map(segment => (
                            <AppTooltip
                              key={`${segment.id}-hit`}
                              side="top"
                              content={
                                <div className="qc-workload-tooltip">
                                  <div className="qc-workload-tooltip-title">{segment.label}</div>
                                  <div>
                                    Assigned studies:{' '}
                                    <span className="qc-lightblue">{segment.assignedStudyCount}</span>
                                  </div>
                                  <div>
                                    Submitted studies:{' '}
                                    <span className="qc-green">{segment.submittedStudyCount}</span>
                                  </div>
                                </div>
                              }
                            >
                              <path
                                className="qc-workload-segment-hit"
                                d={describeDonutSegment(
                                  donutCx,
                                  donutCy,
                                  donutInnerR,
                                  donutOuterR,
                                  segment.startAngle,
                                  segment.endAngle
                                )}
                              />
                            </AppTooltip>
                          ))}
                        </svg>
                      )}

                      {hasData &&
                        segmentsWithAngles.map(segment => (
                        <div
                          key={segment.id}
                          className={`qc-workload-label ${segment.colorClass}`}
                          style={{
                            ...labelPosition(segment.centerRad),
                            transform: 'translate(-50%, -50%)',
                          }}
                        >
                          {segment.percent}%
                        </div>
                      ))}
                    </div>

                    <div className="qc-workload-legend">
                      {hasData ? (
                        segments.map(segment => (
                          <span key={segment.id} className="qc-workload-legend-item">
                            <span className={`qc-workload-dot ${segment.colorClass}`} />{' '}
                            <span className="qc-foot-grey">
                              {segment.label} ({segment.value}%)
                            </span>
                          </span>
                        ))
                      ) : (
                        <span className="qc-foot-grey">No workload data</span>
                      )}
                    </div>
                  </>
                );
              })()}
              </div>
            </section>

            <ProductivityMetrics data={productivityMetricsData} />
          </div>

          <div className="qc-right-blocks qc-right-blocks-workload-escalation">
            <section className="qc-timeperf-card qc-lower-card">
              <h4 className="qc-subsection-title">Time-Based Performance</h4>
              <div className="qc-timeperf-grid">
                {timeBasedPerformanceData.length === 0 ? (
                  <div className="qc-timeperf-row">
                    <div className="qc-timeperf-header">
                      <span className="qc-timeperf-grader">No data</span>
                    </div>
                  </div>
                ) : (
                  timeBasedPerformanceData.map(item => (
                    <div key={item.graderId} className="qc-timeperf-row">
                      <div className="qc-timeperf-header">
                        <span className="qc-timeperf-grader">{item.name}</span>
                        <span className="qc-timeperf-stat">
                          Avg: {item.avgTimeMin.toFixed(2)} min/study
                        </span>
                      </div>
                      <div className="qc-timeperf-metrics">
                        <QcTimeMetricPile
                          className="qc-timeperf-pile"
                          label="Total Outliers"
                          value={item.outliers}
                          valueClassName="qc-blue"
                          icon={faChartLine}
                          onClick={() =>
                            handleNavigateToGraderOutliersStudies({
                              graderId: item.graderId,
                              outlierFilter: 'has_outliers',
                            })
                          }
                          ariaLabel={`View total outlier studies for ${item.name}`}
                        />
                        <QcTimeMetricPile
                          className="qc-timeperf-pile"
                          label={`Excessive (>${qcTimeThresholds.slow} min)`}
                          value={item.excessive}
                          valueClassName="qc-orange"
                          icon={faClock}
                          onClick={() =>
                            handleNavigateToGraderOutliersStudies({
                              graderId: item.graderId,
                              outlierFilter: 'is_excessive',
                            })
                          }
                          ariaLabel={`View excessive-time studies for ${item.name}`}
                        />
                        <QcTimeMetricPile
                          className="qc-timeperf-pile"
                          label={`Too Fast (<${qcTimeThresholds.fast} min)`}
                          value={item.tooFast}
                          valueClassName="qc-red"
                          icon={faBolt}
                          onClick={() =>
                            handleNavigateToGraderOutliersStudies({
                              graderId: item.graderId,
                              outlierFilter: 'is_too_fast',
                            })
                          }
                          ariaLabel={`View too-fast studies for ${item.name}`}
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="qc-radar-card qc-lower-card">
              <h4 className="qc-subsection-title">Review</h4>
              {escalationReviewRadar.graders.length > 0 && (
                <div className="qc-radar-grader-toggles" role="group" aria-label="Grader visibility">
                  {escalationReviewRadar.graders.map(grader => {
                    const isVisible = radarVisibleGraderIds[grader.graderId] !== false;

                    return (
                      <label key={grader.graderId} className="qc-radar-grader-toggle">
                        <input
                          type="checkbox"
                          checked={isVisible}
                          onChange={() =>
                            setRadarVisibleGraderIds(prev => ({
                              ...prev,
                              [grader.graderId]: !isVisible,
                            }))
                          }
                        />
                        <span
                          className="qc-radar-grader-dot"
                          style={{ backgroundColor: grader.color }}
                          aria-hidden
                        />
                        <span>{grader.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              <div
                className={`qc-radar-wrapper${showRadarOutlineOnly ? ' qc-radar-wrapper--outline-only' : ''}`}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarChartData} outerRadius="70%">
                    <PolarGrid stroke="#64748b" radialLines strokeOpacity={0.85} />
                    <PolarAngleAxis
                      dataKey="subject"
                      tick={{ fill: '#e5e7eb', fontSize: 10 }}
                    />
                    <PolarRadiusAxis
                      angle={90}
                      domain={[0, 100]}
                      tick={{ fill: '#94a3b8', fontSize: 9 }}
                      tickCount={6}
                      stroke="#64748b"
                      strokeOpacity={0.85}
                    />
                    {!showRadarOutlineOnly && (
                      <Tooltip
                        cursor={{ fill: 'rgba(148,163,184,0.15)' }}
                        formatter={(value: number) => `${value}%`}
                        contentStyle={{
                          backgroundColor: '#020617',
                          border: '1px solid #1f2937',
                          borderRadius: 6,
                          fontSize: 11,
                          color: '#e5e7eb',
                        }}
                      />
                    )}
                    {showRadarOutlineOnly && (
                      <Radar
                        dataKey="__outline__"
                        stroke="#94a3b8"
                        fill="none"
                        fillOpacity={0}
                        dot={false}
                        activeDot={false}
                        isAnimationActive={false}
                        legendType="none"
                      />
                    )}
                    {visibleRadarGraders.map(grader => (
                      <Radar
                        key={grader.key}
                        name={grader.name}
                        dataKey={grader.key}
                        stroke={grader.color}
                        fill={grader.color}
                        fillOpacity={0.25}
                        dot={{ r: 2.5, stroke: grader.color, strokeWidth: 1 }}
                      />
                    ))}
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </section>
          </div>

          {interGradingAgreement && (
            <div className="qc-right-blocks">
              <section className="qc-agreement-card qc-lower-card">
                <h3 className="qc-section-title">Statistical Agreement Metrics</h3>
                {interGradingAgreement.enabled ? (
                  <>
                    <div className="qc-agreement-legend">
                      <span className="qc-agreement-legend-dot" aria-hidden />
                      <span>Inter-Grader</span>
                    </div>
                    <div className="qc-agreement-chart">
                      <ResponsiveContainer width="100%" height={150}>
                        <BarChart
                          data={interGradingAgreement.chartData}
                          margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                        >
                          <CartesianGrid
                            stroke="#334155"
                            strokeDasharray="4 4"
                            vertical={false}
                          />
                          <XAxis
                            dataKey="name"
                            tick={{ fill: '#e5e7eb', fontSize: 11 }}
                            axisLine={{ stroke: '#475569' }}
                            tickLine={{ stroke: '#475569' }}
                            interval={0}
                          />
                          <YAxis
                            type="number"
                            domain={[0, 100]}
                            ticks={[0, 25, 50, 75, 100]}
                            tick={{ fill: '#e5e7eb', fontSize: 11 }}
                            axisLine={{ stroke: '#475569' }}
                            tickLine={{ stroke: '#475569' }}
                            tickFormatter={value => `${value}%`}
                          />
                          <Tooltip
                            cursor={{ fill: 'rgba(148,163,184,0.12)' }}
                            content={<StatisticalAgreementTooltip />}
                          />
                          <Bar
                            dataKey="agreementPerc"
                            fill="#4285F4"
                            barSize={22}
                            radius={[0, 0, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    {interGradingAgreement.overallAgreementPerc != null && (
                      <div className="qc-agreement-overall">
                        <span className="qc-foot-grey">Overall agreement: </span>
                        <span>{interGradingAgreement.overallAgreementPerc.toFixed(0)}%</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="qc-agreement-disabled">
                    {interGradingAgreement.reason ||
                      'Inter-grading agreement is not available.'}
                  </div>
                )}
              </section>
            </div>
          )}

          {/* additional sections */}
          <div className="qc-right-blocks qc-right-blocks-quality-behavioural">
            <section className="qc-table-card qc-lower-card">
              <h4 className="qc-subsection-title">Annotation Quality Metrics</h4>
              <table className="qc-simple-table">
                <thead>
                  <tr>
                    <th>Grader</th>
                    <th>Escalations</th>
                    <th>Completeness</th>
                    <th>Outliers</th>
                  </tr>
                </thead>
                <tbody>
                  {annotationQualityMetricsData.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="qc-foot-grey">
                        No annotation quality data
                      </td>
                    </tr>
                  ) : (
                    annotationQualityMetricsData.map(row => (
                      <tr key={row.graderId}>
                        <td>{row.name}</td>
                        <td>{row.escalationCount}</td>
                        <td>{row.completenessPerc.toFixed(0)}%</td>
                        <td>{row.outliersCount}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </section>

            <section className="qc-table-card qc-lower-card">
              <h4 className="qc-subsection-title">Behavioral / Operational QC</h4>
              <table className="qc-simple-table">
                <thead>
                  <tr>
                    <th>Grader</th>
                    <th>Login Stability</th>
                    <th>Session Adherence</th>
                    <th>Stability Score</th>
                  </tr>
                </thead>
                <tbody>
                  {behaviouralQcData.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="qc-foot-grey">
                        No behavioural QC data
                      </td>
                    </tr>
                  ) : (
                    behaviouralQcData.map(row => (
                      <tr key={row.graderId}>
                        <td>{row.name}</td>
                        <td>{row.loginStability}</td>
                        <td>{row.sessionAdherence}</td>
                        <td>{row.stabilityScore.toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </section>
          </div>

        </section>
      </div>
            </div>

      <section className="qc-summary-section">
        <h3 className="qc-section-title">QC Overview</h3>
        <div className="qc-summary-grid">
          <section className="qc-passfail-card qc-summary-card">
            <h3 className="qc-section-title">Submission Pass/Fail QC</h3>

            <div className="qc-passfail-table qc-foot-grey">
              <div className="qc-passfail-header">
                <div className="qc-passfail-col qc-passfail-col-rule">Rule / Condition</div>
                <div className="qc-passfail-col qc-passfail-col-result">Result</div>
              </div>
              {(() => {
                const values = submissionBasedQc?.rules?.values as
                  | Array<{ label?: string; result?: number | string; is_pass?: boolean }>
                  | undefined;

                if (!values || !Array.isArray(values) || values.length === 0) {
                  return (
                    <div className="qc-passfail-row">
                      <div className="qc-passfail-col qc-passfail-col-rule">
                        No pass/fail rules available
                      </div>
                      <div className="qc-passfail-col qc-passfail-col-result">—</div>
                    </div>
                  );
                }

                return values.map((item, index) => {
                  const isPass = Boolean(item?.is_pass);
                  const colorClass = isPass ? 'qc-green' : 'qc-red';

                  return (
                    <div className="qc-passfail-row" key={`${item.label ?? 'rule'}-${index}`}>
                      <div className="qc-passfail-col qc-passfail-col-rule">
                        {item.label ?? '—'}
                      </div>
                      <div className="qc-passfail-col qc-passfail-col-result">
                        <span className={colorClass}>{isPass ? 'Pass' : 'Fail'}</span>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>

            {(() => {
              const statusRaw = submissionBasedQc?.rules?.status as string | undefined;
              const status = (statusRaw || '').toString().trim();
              if (!status) {
                return null;
              }

              const isPass = status.toUpperCase() === 'PASS';
              const statusClass = isPass ? 'qc-green' : 'qc-red';

              return (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 18,
                    fontWeight: 600,
                    textAlign: 'center',
                  }}
                >
                  <span className={statusClass}>QC {status.toUpperCase()}</span>
                </div>
              );
            })()}
          </section>

          <section className="qc-qcscore-card qc-lower-card qc-summary-card">
            <h4 className="qc-subsection-title">Grader QC Score (Weighted)</h4>

            <div className="gqc-weight-row">
              <div className="gqc-weight-label">Score<br />Weights</div>
              <div className="gqc-weight-chips">
                <span className="gqc-weight-chip gqc-chip-consistency">
                  {(Number(graderBasedQc?.weightage?.consistency_perc) || 0).toFixed(0)}% Consistency
                </span>
                <span className="gqc-weight-chip gqc-chip-time">
                  {(Number(graderBasedQc?.weightage?.time_stability_perc) || 0).toFixed(0)}% Time Stability
                </span>
                <span className="gqc-weight-chip gqc-chip-protocol">
                  {(Number(graderBasedQc?.weightage?.completeness_perc) || 0).toFixed(0)}% Completeness
                </span>
                <span className="gqc-weight-chip gqc-chip-productivity">
                  {(Number(graderBasedQc?.weightage?.productivity_perc) || 0).toFixed(0)}% Productivity
                </span>
                <span className="gqc-weight-chip gqc-chip-escalation">
                  {(Number(graderBasedQc?.weightage?.escalation_perc) || 0).toFixed(0)}% Escalation
                </span>
                <span className="gqc-weight-chip gqc-chip-time">
                  {(Number(graderBasedQc?.weightage?.behavioural_perc) || 0).toFixed(0)}% Behavioural
                </span>
              </div>
            </div>

            <div className="gqc-bottom-row">
              <div className="gqc-rings">
                {graderQcScoreData.graders.length === 0 ? (
                  <div className="qc-foot-grey">No QC score data</div>
                ) : (
                  graderQcScoreData.graders.map(grader => {
                    const angle = Math.max(
                      4,
                      Math.min(360, (grader.score / 100) * 360)
                    );

                    return (
                      <div className="gqc-ring-item" key={grader.graderId}>
                        <div
                          className="gqc-ring"
                          style={{
                            background: `
                              radial-gradient(circle at center, #020617 56%, transparent 57%),
                              conic-gradient(${grader.color} 0deg ${angle}deg, #111827 ${angle}deg 360deg)
                            `,
                          }}
                        >
                          <div className="gqc-ring-inner">
                            <span className="gqc-ring-value">
                              {grader.score.toFixed(0)}%
                            </span>
                          </div>
                        </div>
                        <div className="gqc-ring-label">{grader.name}</div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="gqc-legend">
                {graderQcScoreData.buckets.length === 0 ? (
                  <div className="qc-foot-grey">No grading configuration</div>
                ) : (
                  graderQcScoreData.buckets.map(bucket => (
                    <div className="gqc-legend-item" key={bucket.id}>
                      <span
                        className="gqc-legend-dot"
                        style={{ background: bucket.color }}
                      />
                      <span>{bucket.description}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>
        </div>
      </section>
          </>
        )}
      </div>

      <InvalidDicomFilesModal
        isOpen={showInvalidFilesModal}
        onClose={() => setShowInvalidFilesModal(false)}
        submissionId={effectiveSubmissionId}
        submissionTitle={invalidFilesSubmissionTitle}
      />
    </div>
  );
};

export default Qc;
